import {
  getSessionEffectiveStatus,
  isCancelledSession,
  isSoftDeletedSession,
  SESSION_STATUS,
} from "./session-status.js";
import { normalizePlannedIds } from "./session-rsvp.js";
import { parseSessionStartMs, sessionDateOnly } from "./session-timing.js";

function nz(v) {
  return String(v ?? "").trim();
}

function isPlannedLiveSession(o) {
  if (!o || isSoftDeletedSession(o)) return false;
  if (isCancelledSession(o)) return false;
  return getSessionEffectiveStatus(o) === SESSION_STATUS.PLANNED;
}

function plannedOverlapCount(a, b) {
  const sa = new Set(normalizePlannedIds(a));
  const sb = new Set(normalizePlannedIds(b));
  if (!sa.size || !sb.size) return 0;
  let n = 0;
  for (const x of sa) {
    if (sb.has(x)) n += 1;
  }
  return n;
}

const RESCHEDULE_UI_EXCLUDED_STATUSES = new Set([
  SESSION_STATUS.CANCELLED_PARTICIPANTS,
]);

function canUseRescheduledUi(session) {
  const status = getSessionEffectiveStatus(session);
  return !RESCHEDULE_UI_EXCLUDED_STATUSES.has(status);
}

/**
 * @param {object} session
 * @param {object[]} allSessions
 * @returns {object|null}
 */
export function findPlannedRescheduleSibling(session, allSessions) {
  if (!session || !Array.isArray(allSessions)) return null;
  if (!isCancelledSession(session)) return null;
  const sid = nz(session.id);
  const dateY = sessionDateOnly(session.date);
  const tid = nz(session.trainer_id);
  const seriesId = nz(session.series_id);
  const tt = nz(session.training_type);
  const tg = nz(session.target_group);
  if (!tid) return null;

  const startMs = parseSessionStartMs(session);
  const plannedA = normalizePlannedIds(session);
  const sessionStatus = getSessionEffectiveStatus(session);
  const isWeatherCancel = sessionStatus === SESSION_STATUS.CANCELLED_WEATHER;

  for (const o of allSessions) {
    if (nz(o.id) === sid) continue;
    if (!isPlannedLiveSession(o)) continue;
    if (nz(o.trainer_id) !== tid) continue;
    if (sessionDateOnly(o.date) !== dateY) continue;
    if (seriesId) {
      if (nz(o.series_id) !== seriesId) continue;
    } else {
      if (isWeatherCancel) continue;
      if (nz(o.training_type) !== tt) continue;
      if (nz(o.target_group) !== tg) continue;
      const plannedB = normalizePlannedIds(o);
      if (plannedA.length === 1 && plannedB.length > 1) continue;
      if (plannedB.length === 1 && plannedA.length > 1) continue;
      const minOv = plannedA.length >= 2 ? 2 : 1;
      if (plannedOverlapCount(session, o) < minOv) continue;
    }
    return o;
  }

  if (!seriesId && plannedA.length >= 2 && Number.isFinite(startMs)) {
    const windowMs = 120 * 24 * 60 * 60 * 1000;
    const minOv = 2;
    for (const o of allSessions) {
      if (nz(o.id) === sid) continue;
      if (!isPlannedLiveSession(o)) continue;
      if (nz(o.trainer_id) !== tid) continue;
      if (nz(o.training_type) !== tt) continue;
      if (nz(o.target_group) !== tg) continue;
      const om = parseSessionStartMs(o);
      if (!Number.isFinite(om) || om <= startMs || om > startMs + windowMs)
        continue;
      if (plannedOverlapCount(session, o) < minOv) continue;
      return o;
    }
  }

  return null;
}

/**
 * @param {object} session
 * @param {object[]} allSessions
 * @returns {boolean}
 */
export function isCancelledSessionWithPlannedSibling(session, allSessions) {
  return findPlannedRescheduleSibling(session, allSessions) != null;
}

/**
 * @param {object[]} pool
 * @returns {Set<string>}
 */
export function buildRescheduleSiblingIdSet(pool) {
  const ids = new Set();
  const list = Array.isArray(pool) ? pool : [];
  for (const s of list) {
    if (!isCancelledSession(s)) continue;
    if (!canUseRescheduledUi(s)) continue;
    if (isCancelledSessionWithPlannedSibling(s, list)) ids.add(String(s.id));
  }
  return ids;
}
